import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { AlertTriangle, Copy, Home, RefreshCw } from 'lucide-react';
import { Accordion } from '@/components/ui/Accordion';
import i18n from '@/lib/i18n';
import { logError } from './ErrorLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** When true, error UI fills the parent instead of covering the whole viewport (keeps app chrome). */
  inline?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

function copyErrorToClipboard(error: Error, errorInfo?: ErrorInfo): string {
  const details = [
    `Error: ${error.toString()}`,
    error.stack ? `Stack: ${error.stack}` : '',
    errorInfo?.componentStack ? `Component: ${errorInfo.componentStack}` : '',
    `User agent: ${navigator.userAgent}`,
    `URL: ${window.location.href}`,
  ]
    .filter(Boolean)
    .join('\n\n');
  return details;
}

/**
 * React Error Boundary with graceful error recovery
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error with context
    logError('React Error Boundary caught an error', error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      metadata: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      },
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Store error info in state
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    // Full page reload to ensure a clean state after a fatal error
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleCopyError = async () => {
    const { error, errorInfo } = this.state;
    if (!error) return;
    const details = copyErrorToClipboard(error, errorInfo);
    const copyLabel = i18n.t('errors.copyDetails');
    const copiedLabel = i18n.t('errors.copied');
    try {
      await navigator.clipboard.writeText(details);
      const btn = document.querySelector('[data-error-copy-btn]') as HTMLButtonElement;
      if (btn) {
        const originalText = btn.textContent ?? copyLabel;
        btn.textContent = copiedLabel;
        setTimeout(() => (btn.textContent = originalText), 1500);
      }
    } catch (e) {
      console.error('Error details (clipboard failed):', details, e);
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className={
            this.props.inline
              ? 'min-h-[50vh] w-full flex items-center justify-center p-4'
              : 'fixed inset-0 z-[1000] bg-light-50/90 backdrop-blur-sm overflow-auto'
          }
        >
          <div
            className={
              this.props.inline
                ? 'w-full flex items-center justify-center'
                : 'min-h-full flex items-center justify-center p-4'
            }
          >
            <div className='max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center'>
              <div className='mb-6'>
                <AlertTriangle className='h-16 w-16 text-red-500 mx-auto mb-4' />
                <h1 className='text-2xl font-bold text-foreground mb-2'>{i18n.t('errors.boundaryTitle')}</h1>
                <p className='text-gray-700 dark:text-gray-300'>{i18n.t('errors.boundaryDescription')}</p>
              </div>

              {/* Error details in development */}
              {import.meta.env.DEV && this.state.error && (
                <div className='mb-6 p-4 bg-gray-100 dark:bg-gray-900 rounded text-left text-sm'>
                  <h3 className='font-semibold mb-2'>{i18n.t('errors.devDetailsTitle')}</h3>
                  <pre className='whitespace-pre-wrap text-xs text-red-600'>{this.state.error.toString()}</pre>
                  {this.state.errorInfo && (
                    <Accordion
                      className='mt-2 border-gray-300 dark:border-gray-600 bg-transparent'
                      title={i18n.t('errors.stackTrace')}
                      summaryClassName='text-gray-700 dark:text-gray-300 px-0 py-1'
                      contentClassName='px-0 pb-0 pt-2 border-t-0'
                    >
                      <pre className='whitespace-pre-wrap text-xs mt-2'>{this.state.errorInfo.componentStack}</pre>
                    </Accordion>
                  )}
                </div>
              )}

              <div className='flex flex-col gap-3'>
                <div className='flex flex-col sm:flex-row gap-3'>
                  <button
                    type='button'
                    onClick={this.handleRetry}
                    className='flex-1 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2'
                  >
                    <RefreshCw className='h-4 w-4' />
                    {i18n.t('errors.retry')}
                  </button>
                  <button
                    type='button'
                    onClick={this.handleGoHome}
                    className='flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2'
                  >
                    <Home className='h-4 w-4' />
                    {i18n.t('errors.goHome')}
                  </button>
                </div>

                {this.state.error && (
                  <button
                    data-error-copy-btn
                    type='button'
                    onClick={this.handleCopyError}
                    className='w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                  >
                    <Copy className='h-4 w-4 shrink-0' />
                    {i18n.t('errors.copyDetails')}
                  </button>
                )}
              </div>

              <p className='text-xs text-gray-600 dark:text-gray-400 mt-4 text-center'>
                {i18n.t('errors.contactSupport')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
